import { useState, useEffect, useRef, useCallback } from "react";
import { Device } from "@twilio/voice-sdk";
import api from "../services/api";

const useTwilioDevice = (enabled = true, authUserId = "") => {
  const [device, setDevice] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isInCall, setIsInCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callStatus, setCallStatus] = useState("");
  const [error, setError] = useState(null);

  const durationIntervalRef = useRef(null);
  const deviceRef = useRef(null);
  const callRef = useRef(null);

  // Guards ported from call-center app — prevent spurious disconnect
  const callAcceptedTimestampRef = useRef(0);
  const acceptedCallSidRef = useRef(null);
  const callEndedRef = useRef(null);
  const processedCallSids = useRef(new Set());
  const isExplicitHangupRef = useRef(false);

  // Tracks mute intent set before the call connects (during ringing)
  const pendingMuteRef = useRef(false);

  const startDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
    setCallDuration(0);
    const start = Date.now();
    durationIntervalRef.current = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - start) / 1000));
    }, 1000);
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const handleCallEnd = useCallback(
    (call) => {
      const callSid =
        call?.parameters?.CallSid || callRef.current?.parameters?.CallSid;

      // Capture explicit hangup state BEFORE clearing
      const wasExplicitHangup = isExplicitHangupRef.current;

      // CRITICAL GUARD: If a call was accepted < 2 seconds ago, block end events
      // UNLESS the agent explicitly clicked hang up. Twilio SDK sometimes fires
      // spurious disconnect events immediately after accept.
      const timeSinceAccept = Date.now() - callAcceptedTimestampRef.current;
      if (
        callAcceptedTimestampRef.current > 0 &&
        timeSinceAccept < 2000 &&
        !wasExplicitHangup
      ) {
        return;
      }

      if (wasExplicitHangup) {
        isExplicitHangupRef.current = false;
      }

      // Prevent duplicate end handling for the same call
      if (callEndedRef.current === callSid) return;
      callEndedRef.current = callSid;
      setTimeout(() => {
        if (callEndedRef.current === callSid) callEndedRef.current = null;
      }, 3000);

      // Clear accepted-call tracking
      if (acceptedCallSidRef.current === callSid) {
        acceptedCallSidRef.current = null;
        callAcceptedTimestampRef.current = 0;
      }

      stopDurationTimer();
      callRef.current = null;
      pendingMuteRef.current = false;
      setCurrentCall(null);
      setIsInCall(false);
      setIsMuted(false);
      setCallStatus("completed");

      if (callSid) processedCallSids.current.delete(callSid);

      window.dispatchEvent(
        new CustomEvent("twilio-call-ended", { detail: { callSid } })
      );
    },
    [stopDurationTimer]
  );

  // Initialize device
  useEffect(() => {
    if (!enabled) {
      if (deviceRef.current) {
        try {
          deviceRef.current.destroy();
        } catch (_) {}
        deviceRef.current = null;
      }
      setDevice(null);
      setCurrentCall(null);
      setIsReady(false);
      setIsInCall(false);
      setIsMuted(false);
      setCallDuration(0);
      setCallStatus("");
      localStorage.setItem("twilioDeviceReady", "false");
      return;
    }

    let cancelled = false;
    setError(null);

    const initDevice = async () => {
      try {
        const res = await api.get("/calls/token");
        const { token } = res.data?.data || res.data || {};
        if (cancelled) return;
        if (!token) {
          if (!cancelled) {
            setError("No voice token returned from server");
          }
          return;
        }

        const newDevice = new Device(token, {
          logLevel: 1,
          closeProtection: true,
        });

        newDevice.on("registered", () => {
          console.log(`📞 [TWILIO-SDK] Device registered (cancelled=${cancelled})`);
          if (cancelled) return;
          setIsReady(true);
          localStorage.setItem("twilioDeviceReady", "true");
        });

        newDevice.on("unregistered", () => {
          setIsReady(false);
          localStorage.setItem("twilioDeviceReady", "false");
        });

        newDevice.on("error", (err) => {
          console.error("Twilio Device error:", err);
          setError(err.message);
        });

        newDevice.on("tokenWillExpire", async () => {
          try {
            const refreshRes = await api.get("/calls/token");
            const { token: newToken } =
              refreshRes.data?.data || refreshRes.data || {};
            if (newToken) newDevice.updateToken(newToken);
          } catch (err) {
            console.error("Error refreshing Twilio token:", err);
          }
        });

        newDevice.on("incoming", (call) => {
          const callSid = call.parameters?.CallSid;
          console.log(`📞 [TWILIO-SDK] incoming call: ${callSid}`);

          // If this Device instance is from a cancelled useEffect (React StrictMode),
          // don't accept — the Device is being destroyed and would drop the call.
          if (cancelled) {
            console.warn(`📞 [TWILIO-SDK] Ignoring incoming on cancelled Device: ${callSid}`);
            return;
          }

          // Prevent duplicate processing — Twilio sometimes sends multiple invites
          if (processedCallSids.current.has(callSid)) {
            console.warn(`📞 [TWILIO-SDK] Duplicate incoming ignored: ${callSid}`);
            return;
          }
          processedCallSids.current.add(callSid);
          setTimeout(() => processedCallSids.current.delete(callSid), 120000);

          // Track per-call accept state (closure-scoped, not ref)
          let callWasAccepted = false;
          let acceptTimestamp = 0;

          // CRITICAL: register event listeners BEFORE calling accept()
          call.on("accept", () => {
            callWasAccepted = true;
            acceptTimestamp = Date.now();
            console.log(`📞 [TWILIO-SDK] call ACCEPTED: ${callSid}`);

            // Set refs to block handleCallEnd for 2 seconds
            acceptedCallSidRef.current = callSid;
            callAcceptedTimestampRef.current = Date.now();

            callRef.current = call;
            setCurrentCall(call);
            setIsInCall(true);
            setCallStatus("connected");
            startDurationTimer();

            // Apply mute if the agent toggled it during ringing
            if (pendingMuteRef.current) {
              try { call.mute(true); } catch (_) {}
            }

            window.dispatchEvent(
              new CustomEvent("twilio-call-started", {
                detail: { callSid },
              })
            );
          });

          call.on("disconnect", () => {
            const timeSinceAccept = Date.now() - acceptTimestamp;
            console.log(`📞 [TWILIO-SDK] disconnect: ${callSid} wasAccepted=${callWasAccepted} timeSinceAccept=${timeSinceAccept}ms explicit=${isExplicitHangupRef.current}`);
            // Allow explicit hangups to bypass all guards
            if (isExplicitHangupRef.current) {
              handleCallEnd(call);
              return;
            }
            // Block disconnect events that arrive within 2s of accept
            if (!callWasAccepted) return;
            if (timeSinceAccept < 2000) {
              console.warn(`📞 [TWILIO-SDK] BLOCKED spurious disconnect (${timeSinceAccept}ms < 2000ms): ${callSid}`);
              return;
            }
            handleCallEnd(call);
          });

          call.on("cancel", () => {
            console.log(`📞 [TWILIO-SDK] cancel: ${callSid} wasAccepted=${callWasAccepted}`);
            if (callWasAccepted) return;
            handleCallEnd(call);
          });

          call.on("reject", () => {
            console.log(`📞 [TWILIO-SDK] reject: ${callSid} wasAccepted=${callWasAccepted}`);
            if (callWasAccepted) return;
            handleCallEnd(call);
          });

          call.on("error", (err) => {
            console.error(`📞 [TWILIO-SDK] error: ${callSid}`, err?.code, err?.message);
            const code = err?.code;
            if ([31003, 31005, 31009].includes(code)) return;
            handleCallEnd(call);
          });

          call.on("mute", (muted) => setIsMuted(muted));

          call.on("reconnecting", (err) => {
            console.warn(`📞 [TWILIO-SDK] reconnecting: ${callSid}`, err?.message);
          });

          call.on("reconnected", () => {
            console.log(`📞 [TWILIO-SDK] reconnected: ${callSid}`);
          });

          call.on("warning", (name, data) => {
            console.warn(`📞 [TWILIO-SDK] warning: ${callSid} ${name}`, data);
          });

          // NOW accept the call — listeners are already in place
          try {
            console.log(`📞 [TWILIO-SDK] Calling accept() for: ${callSid}`);
            call.accept();
          } catch (err) {
            console.error(`📞 [TWILIO-SDK] Failed to accept: ${callSid}`, err);
            handleCallEnd(call);
          }
        });

        await newDevice.register();
        if (cancelled) {
          console.warn("📞 [TWILIO-SDK] Device cancelled after register — destroying");
          newDevice.destroy();
          return;
        }
        deviceRef.current = newDevice;
        setDevice(newDevice);
        // Some environments fire `registered` late or not at all; register() has resolved.
        setIsReady(true);
        console.log("📞 [TWILIO-SDK] Device ready and stored in ref");
      } catch (err) {
        if (!cancelled) {
          console.error("Error initializing Twilio Device:", err);
          const msg =
            err?.error?.message ||
            err?.message ||
            (typeof err === "string" ? err : null) ||
            "Twilio Device failed to start";
          setError(msg);
        }
      }
    };

    initDevice();

    return () => {
      cancelled = true;
      stopDurationTimer();
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
      localStorage.setItem("twilioDeviceReady", "false");
    };
  }, [enabled, authUserId]);

  const makeCall = useCallback(
    async (params) => {
      if (!deviceRef.current) throw new Error("Twilio Device not ready");

      setCallStatus("initiating");

      const call = await deviceRef.current.connect({ params });

      call.on("ringing", () => {
        setCallStatus("ringing");
      });

      call.on("accept", () => {
        callRef.current = call;
        setCurrentCall(call);
        setIsInCall(true);
        setCallStatus("connected");
        startDurationTimer();

        if (pendingMuteRef.current) {
          try { call.mute(true); } catch (_) {}
        }

        window.dispatchEvent(
          new CustomEvent("twilio-call-started", {
            detail: { callSid: call.parameters?.CallSid },
          })
        );
      });

      call.on("disconnect", () => handleCallEnd(call));
      call.on("cancel", () => handleCallEnd(call));
      call.on("reject", () => handleCallEnd(call));
      call.on("error", (err) => {
        console.error("Twilio call error:", err);
        handleCallEnd(call);
      });
      call.on("mute", (muted) => setIsMuted(muted));

      return call;
    },
    [startDurationTimer, handleCallEnd]
  );

  const hangUp = useCallback(() => {
    // Mark as explicit hangup FIRST — bypasses the 2-second guard
    isExplicitHangupRef.current = true;
    setTimeout(() => {
      if (isExplicitHangupRef.current) isExplicitHangupRef.current = false;
    }, 1000);

    const call = callRef.current;
    if (call) {
      try {
        call.disconnect();
      } catch (e) {
        console.warn("Error disconnecting call:", e);
      }
      // Safety: if the disconnect event doesn't fire within 2s, force-clear state
      setTimeout(() => {
        if (callRef.current === call) {
          handleCallEnd(call);
        }
      }, 2000);
    } else {
      handleCallEnd(null);
    }
  }, [handleCallEnd]);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (call) {
      try {
        const newMuted = !call.isMuted();
        call.mute(newMuted);
        pendingMuteRef.current = newMuted;
        setIsMuted(newMuted);
      } catch (e) {
        console.error("📞 [TWILIO-SDK] toggleMute error:", e);
      }
    } else {
      const newMuted = !pendingMuteRef.current;
      pendingMuteRef.current = newMuted;
      setIsMuted(newMuted);
    }
  }, []);

  const sendDigits = useCallback((digits) => {
    if (callRef.current) {
      callRef.current.sendDigits(digits);
    }
  }, []);

  return {
    device,
    currentCall,
    isReady,
    isInCall,
    isMuted,
    callDuration,
    callStatus,
    error,
    makeCall,
    hangUp,
    toggleMute,
    sendDigits,
    setCallStatus,
  };
};

export default useTwilioDevice;
